import React, { useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'components/buttons/Button';
import { LinkButton } from 'components/buttons/LinkButton';
import { TextField } from 'components/inputs/TextField.react';

import { useLoginFlow } from '../contexts/LoginFlowContext';
import { useLogin } from '../hooks/useLogin';
import type { SsoProvider } from '../types/login';

import { ErrorMessage } from './common/ErrorMessage';
import { SsoProviders } from './SsoProviders';

export const LoginView = () => {
    const { returnUrl } = useLoginFlow();

    const {
        email,
        step,
        showPassword,
        ssoProviders,
        isLoadingProviders,
        providersError,
        isLoggingIn,
        loginError,
        submitEmail,
        submitPassword,
        switchAccount,
        goToPasswordStep,
    } = useLogin();

    const publicSsoProviders = usePublicSsoProviders();

    return (
        <div>
            {publicSsoProviders.length > 0 && step === 'email' && (
                <SsoProviders providers={publicSsoProviders} returnUrl={returnUrl} />
            )}

            {step === 'email' && (
                <EmailStep
                    defaultEmail=""
                    isLoading={isLoadingProviders}
                    error={providersError}
                    onSubmit={submitEmail}
                    showDivider={publicSsoProviders.length > 0}
                />
            )}

            {step === 'providers' && (
                <ProviderStep
                    email={email}
                    showPassword={showPassword}
                    ssoProviders={ssoProviders}
                    returnUrl={returnUrl}
                    onPasswordLogin={goToPasswordStep}
                    onSwitchAccount={switchAccount}
                />
            )}

            {step === 'password' && (
                <PasswordStep
                    email={email}
                    isLoading={isLoggingIn}
                    error={loginError}
                    onSubmit={submitPassword}
                    onSwitchAccount={switchAccount}
                />
            )}

            <div id="turnstile-widget-login" className="tw-flex tw-justify-center tw-mt-12" />
        </div>
    );
};

// -------------------------------------------------------------------
// Sub-components
// -------------------------------------------------------------------

interface EmailStepProps {
    defaultEmail: string;
    isLoading: boolean;
    error?: string;
    onSubmit: (email: string) => void;
    showDivider: boolean;
}

const EmailStep = ({ defaultEmail, isLoading, error, onSubmit, showDivider }: EmailStepProps) => {
    const intl = useIntl();
    const [emailValue, setEmailValue] = useState(defaultEmail);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(emailValue.trim());
    };

    return (
        <>
            {showDivider && (
                <div className="tw-flex tw-items-center tw-gap-12 tw-my-16">
                    <div className="tw-flex-1 tw-h-[1px] tw-bg-neutral-border" />
                    <span className="tw-text-neutral-subdued tw-text-l tw-uppercase">
                        <FormattedMessage id="login.or" defaultMessage="or" />
                    </span>
                    <div className="tw-flex-1 tw-h-[1px] tw-bg-neutral-border" />
                </div>
            )}
            <form onSubmit={handleSubmit}>
                <input
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    className="tw-hidden"
                    tabIndex={-1}
                    aria-hidden="true"
                />
                <TextField
                    name="email"
                    type="email"
                    required
                    placeholderText={intl.formatMessage({
                        id: 'login.email.placeholder',
                        defaultMessage: 'Enter your email',
                    })}
                    value={emailValue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEmailValue(e.target.value)
                    }
                    autoFocus
                    autoComplete="username"
                    className=""
                />
                <ErrorMessage message={error} />
                <div className="tw-mt-16">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        wrapperClasses="tw-w-full"
                    >
                        <FormattedMessage
                            id="login.continueWithEmail"
                            defaultMessage="Continue with Email"
                        />
                    </Button>
                </div>
            </form>
        </>
    );
};

interface ProviderStepProps {
    email: string;
    showPassword: boolean;
    ssoProviders: SsoProvider[];
    returnUrl: string;
    onPasswordLogin: () => void;
    onSwitchAccount: () => void;
}

const ProviderStep = ({
    email,
    showPassword,
    ssoProviders,
    returnUrl,
    onPasswordLogin,
    onSwitchAccount,
}: ProviderStepProps) => {
    const intl = useIntl();

    return (
        <div className="tw-text-center">
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
                <FormattedMessage
                    id="login.signingInAs"
                    defaultMessage="Signing in as {email}"
                    values={{ email: <strong>{email}</strong> }}
                />
            </p>

            {ssoProviders.length > 0 && (
                <SsoProviders
                    providers={ssoProviders}
                    returnUrl={returnUrl}
                    showDivider={showPassword}
                />
            )}

            {showPassword && (
                <div className="tw-mt-12">
                    <Button variant="primary" wrapperClasses="tw-w-full" onClick={onPasswordLogin}>
                        <FormattedMessage
                            id="login.signInWithPassword"
                            defaultMessage="Sign in with password"
                        />
                    </Button>
                </div>
            )}

            <div className="tw-mt-16 tw-flex tw-justify-center">
                <LinkButton
                    size="small"
                    text={intl.formatMessage({
                        id: 'login.switchAccount',
                        defaultMessage: 'Sign in with a different account',
                    })}
                    onClick={onSwitchAccount}
                />
            </div>
        </div>
    );
};

interface PasswordStepProps {
    email: string;
    isLoading: boolean;
    error?: string;
    onSubmit: (password: string) => void;
    onSwitchAccount: () => void;
}

const PasswordStep = ({
    email,
    isLoading,
    error,
    onSubmit,
    onSwitchAccount,
}: PasswordStepProps) => {
    const intl = useIntl();
    const [password, setPassword] = useState('');

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (password) {
            onSubmit(password);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <input
                type="text"
                name="username"
                autoComplete="username"
                value={email}
                readOnly
                className="tw-hidden"
                tabIndex={-1}
                aria-hidden="true"
            />
            <p className="tw-text-neutral-subdued tw-text-s tw-mb-16">
                <FormattedMessage
                    id="login.signingInAs"
                    defaultMessage="Signing in as {email}"
                    values={{ email: <strong>{email}</strong> }}
                />
            </p>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                <label htmlFor="password" className="tw-font-medium tw-text-s tw-text-neutral">
                    <FormattedMessage id="login.password.label" defaultMessage="Password" />
                </label>
                <LinkButton
                    size="small"
                    text={intl.formatMessage({
                        id: 'login.forgotPassword',
                        defaultMessage: 'Forgot password?',
                    })}
                    url="/forgot-password"
                />
            </div>
            <TextField
                name="password"
                password
                placeholderText={intl.formatMessage({
                    id: 'login.password.placeholder',
                    defaultMessage: 'Enter your password',
                })}
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                autoFocus
                autoComplete="current-password"
                className=""
            />
            <ErrorMessage message={error} />
            <div className="tw-mt-16">
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading}
                    wrapperClasses="tw-w-full"
                >
                    <FormattedMessage id="login.signIn" defaultMessage="Sign in" />
                </Button>
            </div>
            <div className="tw-mt-16 tw-text-center">
                <LinkButton
                    size="small"
                    text={intl.formatMessage({
                        id: 'login.switchAccount',
                        defaultMessage: 'Sign in with a different account',
                    })}
                    onClick={onSwitchAccount}
                />
            </div>
        </form>
    );
};

// Server-rendered JSON uses PascalCase (Newtonsoft), map to camelCase
const mapServerProvider = (raw: Record<string, unknown>): SsoProvider => ({
    id: raw.Id as number,
    name: raw.Name as string,
    logoUrl: raw.LogoUrl as string | undefined,
    issuer: raw.Issuer as string | undefined,
    callbackPath: raw.CallbackPath as string | undefined,
    isPublicProvider: raw.IsPublicProvider as boolean | undefined,
});

const usePublicSsoProviders = (): SsoProvider[] => {
    const [providers] = useState<SsoProvider[]>(() => {
        try {
            const win = window as unknown as Record<string, unknown>;
            const raw = win.PUBLIC_SSO_PROVIDERS as string | undefined;
            if (!raw) return [];

            const parser = new DOMParser();
            const doc = parser.parseFromString(raw, 'text/html');
            const decoded = doc.documentElement.textContent ?? '';
            const parsed = JSON.parse(decoded) as Record<string, unknown>[];
            return parsed.map(mapServerProvider);
        } catch {
            return [];
        }
    });
    return providers;
};
